/**
 * LegalTakedownPage — public form for opt-out / right-of-publicity removal.
 *
 * Reachable at `/legal/takedown` (no auth, by design). Real people or
 * their representatives can request removal of their name + likeness
 * from the Fantasy World Star catalogue. Submissions are stored in the
 * `takedown_requests` collection and reviewed by an admin.
 *
 * Per the v5.7.24 legal risk assessment, this form is the single most
 * defensive mitigation available: it demonstrates an active, low-
 * friction takedown channel which weighs heavily against claims of
 * willful disregard for the subject's rights.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle2, AlertCircle, Copy } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import TurnstileWidget from '../components/TurnstileWidget';

const RELATIONSHIP_OPTIONS = [
  { value: 'self',            label: "I am this person" },
  { value: 'agent',           label: 'I represent this person (agent/lawyer/publicist)' },
  { value: 'parent_guardian', label: 'Parent / legal guardian of this person' },
  { value: 'estate',          label: 'Executor / heir of this person\'s estate' },
  { value: 'other',           label: 'Other (explain below)' },
];

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1 flex items-center gap-1.5">
        <span style={{ fontFamily: 'var(--font-heading)' }}>{label}</span>
        {required && <span className="text-destructive">*</span>}
      </div>
      {hint && <div className="text-xs text-muted-foreground mb-1.5">{hint}</div>}
      {children}
    </label>
  );
}

const inputClass =
  'w-full px-3 py-2 rounded-md border border-border/60 bg-card/60 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition';

export default function LegalTakedownPage() {
  const navigate = useNavigate();

  // 2026-06-17 parity audit (P3 takedown-context) — pre-fill the person from
  // ?entityName=&entityId= so an opt-out link from a profile lands ready to go
  // (mirrors the mobile LegalTakedownScreen route-param prefill).
  const _q = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search) : null;

  const [form, setForm] = useState({
    your_name: '',
    your_email: '',
    relationship: 'self',
    entity_name: (_q?.get('entityName') || '').slice(0, 200),
    entity_id: (_q?.get('entityId') || '').slice(0, 100),
    reason: '',
    verification_url: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt]       = useState(null);
  const [error, setError]           = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);  // bump to remount widget on submit failure
  const turnstileEnabled = Boolean((process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim());

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.your_name || !form.your_email || !form.entity_name) {
      setError('Please fill the required fields.');
      return;
    }
    if (turnstileEnabled && !turnstileToken) {
      setError('Please complete the bot-verification challenge.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/legal/takedown', form, {
        headers: turnstileToken ? { 'X-Turnstile-Token': turnstileToken } : {},
      });
      setReceipt(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Submission failed. Please email contact@gazumbagames.com directly.';
      setError(String(msg));
      // AUDIT 2026-06 (P2/P3): the Turnstile token is single-use — remount the
      // widget so a retry after a failed submit gets a fresh token (else the
      // backend rejects the stale one and the user is stuck).
      setTurnstileToken(null);
      setTurnstileKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  if (receipt) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-xl mx-auto pt-12">
          <CheckCircle2 className="h-12 w-12 text-[hsl(143,71%,45%)] mb-4" />
          <h1
            className="text-3xl font-bold mb-3"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Request received
          </h1>
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
            {receipt.message}
          </p>
          <div className="rounded-xl border border-border/60 bg-card/50 p-4 mb-6">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
              Your reference number
            </div>
            <div className="flex items-center justify-between gap-3">
              <code
                className="text-2xl font-bold tracking-widest"
                style={{ fontFamily: 'var(--font-heading)' }}
                data-testid="takedown-reference"
              >
                {receipt.reference}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(receipt.reference);
                  toast.success('Copied');
                }}
                className="p-2 hover:bg-secondary/60 rounded transition-colors"
                aria-label="Copy reference"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-6">
            If you need to follow up, write to{' '}
            <a href={`mailto:${receipt.contact}`} className="underline">
              {receipt.contact}
            </a>{' '}
            and quote your reference. We aim to review requests within 7
            business days.
          </p>
          <Button onClick={() => navigate('/')} variant="outline" data-testid="takedown-home">
            Return to home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6" data-testid="legal-takedown-page">
      <div className="max-w-xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
          data-testid="takedown-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <h1
          className="text-3xl sm:text-4xl font-bold mb-2"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          Takedown request
        </h1>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Request removal of a person's name, photo, or entry from
          Fantasy World Star. No account required. We aim to review
          within 7 business days; approved requests result in immediate
          removal from all public lists, scoring, and rosters.
        </p>

        <form onSubmit={submit} className="space-y-5">
          <Field label="Your full name" required>
            <input
              required
              type="text"
              value={form.your_name}
              onChange={update('your_name')}
              className={inputClass}
              data-testid="td-name"
            />
          </Field>

          <Field
            label="Your email"
            hint="We'll use this to confirm and follow up. Not displayed publicly."
            required
          >
            <input
              required
              type="email"
              value={form.your_email}
              onChange={update('your_email')}
              className={inputClass}
              data-testid="td-email"
            />
          </Field>

          <Field label="Your relationship to the person" required>
            <select
              value={form.relationship}
              onChange={update('relationship')}
              className={inputClass}
              data-testid="td-relationship"
            >
              {RELATIONSHIP_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Name of the person to remove" required>
            <input
              required
              type="text"
              value={form.entity_name}
              onChange={update('entity_name')}
              className={inputClass}
              data-testid="td-entity-name"
              placeholder="e.g. Lionel Messi"
            />
          </Field>

          <Field
            label="Entity ID (optional, speeds review)"
            hint="The reference shown on the person's detail page URL, e.g. /person/abc-123."
          >
            <input
              type="text"
              value={form.entity_id}
              onChange={update('entity_id')}
              className={inputClass}
              data-testid="td-entity-id"
              placeholder="Optional"
            />
          </Field>

          <Field
            label="Identity / authority verification"
            hint="A link we can use to verify your identity or authority — your verified social profile, the person's Wikipedia page, the law firm's letterhead URL, etc."
          >
            <input
              type="url"
              value={form.verification_url}
              onChange={update('verification_url')}
              className={inputClass}
              data-testid="td-verify"
              placeholder="https://..."
            />
          </Field>

          <Field
            label="Reason (optional)"
            hint="What harm is being caused, or what right is being asserted? Helps us prioritise."
          >
            <textarea
              rows={4}
              value={form.reason}
              onChange={update('reason')}
              maxLength={2000}
              className={inputClass}
              data-testid="td-reason"
            />
          </Field>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-md border border-destructive/40 bg-destructive/10 text-destructive text-sm" data-testid="td-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {turnstileEnabled && (
            <TurnstileWidget key={turnstileKey} onToken={setTurnstileToken} />
          )}

          <Button
            type="submit"
            disabled={submitting || (turnstileEnabled && !turnstileToken)}
            className="w-full"
            data-testid="td-submit"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? 'Submitting…' : 'Submit takedown request'}
          </Button>

          <p className="text-xs text-muted-foreground text-center pt-2 leading-relaxed">
            By submitting you confirm the information above is accurate.
            Knowingly false takedown requests may be subject to civil
            penalties.
          </p>
        </form>
      </div>
    </div>
  );
}
